import { useState, useMemo, useEffect, useRef } from "react";
import toast, { Toaster } from "react-hot-toast";
import { getRowStyle } from "../utils/utils";
import { getIncompleteWorkOrders, assignRoom } from "../api/workOrder";
import SingleDatePicker from "../components/SingleDatePicker";
import normalizeAllergen from "../utils/normalizeAllergen";
import { X } from "lucide-react";
import { rooms } from "../constants/rooms";
import ReassignModal from "../modals/ReassignModal";
import { useAuth } from "../context/AuthContext";
import { updateProduct } from "../api/ProductsApis";
import AssignErrorModal from "../modals/AssignErrorModal";
import { AnimatePresence } from "framer-motion";

const SkeletonRow = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 12 }).map((_, i) => (
      <td
        key={i}
        className={`py-3 px-4 border-b border-gray-200 ${
          i === 3 ? "" : "whitespace-nowrap"
        }`}
      >
        <div className="h-4 bg-gray-200 rounded"></div>
      </td>
    ))}
  </tr>
);

const HEADER_TO_KEY = {
  "WO Number": "woNumber",
  Project: "projectNumber",
  SKU: "sku",
  Title: "title",
  Label: "labelCategory",
  Allergen: "allergen",
  "Net Weight": "netWeight",
  "Planned Qty": "plannedQty",
  "Actual Qty": "actualQty",
  Room: "room",
  Date: "date",
};

const Alerts = () => {
  const [filters, setFilters] = useState({
    woNumber: "",
    projectNumber: "",
    date: null,
  });
  const [debouncedWoNumber, setDebouncedWoNumber] = useState("");
  const [debouncedProjectNumber, setDebouncedProjectNumber] = useState("");

  const [incompleteWorkOrders, setIncompleteWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;

  const woInputRef = useRef(filters.woNumber);
  const projectInputRef = useRef(filters.projectNumber);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalWO, setModalWO] = useState(null);
  const [showAssignErrorModal, setShowAssignErrorModal] = useState(false);
  const [assignError, setAssignError] = useState(null);
  const [pendingWorkOrder, setPendingWorkOrder] = useState(null);
  const [thresholdHours, setThresholdHours] = useState(25);
  const { canWrite } = useAuth();
  const canAssign = canWrite("ERPagAlerts");

  useEffect(() => {
    woInputRef.current = filters.woNumber;
  }, [filters.woNumber]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedWoNumber(woInputRef.current),
      300,
    );
    return () => clearTimeout(timer);
  }, [filters.woNumber]);

  useEffect(() => {
    projectInputRef.current = filters.projectNumber;
  }, [filters.projectNumber]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedProjectNumber(projectInputRef.current),
      300,
    );
    return () => clearTimeout(timer);
  }, [filters.projectNumber]);

  const effectiveFilters = {
    woNumber: debouncedWoNumber,
    projectNumber: debouncedProjectNumber,
    date: filters.date,
  };

  const fetchWorkOrders = async () => {
    const controller = new AbortController();
    try {
      setLoading(true);
      setError(null);
      const response = await getIncompleteWorkOrders({
        signal: controller.signal,
      });
      const currentThreshold = response?.data?.thresholdHours || 25;
      setThresholdHours(currentThreshold);
      const now = Date.now();

      const transformed = response.data.data.map((wo) => ({
        _id: wo._id,
        woNumber: wo.woNumber,
        projectNumber: wo.projectNumber,
        sku: wo.sku,
        title: wo.title,
        labelCategory: wo.label,
        allergen: normalizeAllergen(wo.allergens),
        netWeight: wo.netWeight,
        plannedQty: wo.plannedQty,
        actualQty: wo.actualQty,
        room: wo.room,
        date: wo.assignedDate
          ? new Date(wo.assignedDate).toISOString().split("T")[0]
          : "",
        alertMessage: (() => {
          if (!wo.assignedDate) return "Old open work order - pending closure";
          const ageMs = now - new Date(wo.assignedDate).getTime();
          if (Number.isNaN(ageMs)) return "Old open work order - pending closure";
          const ageHours = Math.floor(ageMs / 3600000);
          return `Old by ${Math.max(currentThreshold, ageHours)}h - pending closure`;
        })(),
      }));

      setIncompleteWorkOrders(transformed);
      toast.success(`Loaded ${response.data.count ?? 0} incomplete WOs`, {
        duration: 4000,
        position: "top-right",
        id: "fetch-success",
      });
    } catch (err) {
      if (err.name === "CanceledError") return;
      const msg = err.message || "Failed to load data";
      setError(msg);
      if (err?.response?.status !== 401) {
        toast.error(msg, { id: "fetch-error" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkOrders();
  }, []);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilter = (key) => {
    updateFilter(key, key === "date" ? null : "");
  };

  const clearFilters = () => {
    setFilters({ woNumber: "", projectNumber: "", date: null });
    setCurrentPage(1);
    toast.success("Filters cleared!", {
      id: "clear-filters",
      position: "top-right",
    });
  };

  const formatLocalDate = (date) => {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const filteredWorkOrders = useMemo(() => {
    return (incompleteWorkOrders || []).filter((wo) => {
      const matchesWoNumber = wo.woNumber
        ?.toLowerCase()
        .includes(effectiveFilters.woNumber.toLowerCase());
      const matchesProjectNumber = wo.projectNumber
        ?.toLowerCase()
        .includes(effectiveFilters.projectNumber.toLowerCase());
      const matchesDate =
        !effectiveFilters.date ||
        wo.date === formatLocalDate(effectiveFilters.date);
      return matchesWoNumber && matchesProjectNumber && matchesDate;
    });
  }, [incompleteWorkOrders, effectiveFilters]);

  const totalItems = filteredWorkOrders.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const paginatedWorkOrders = filteredWorkOrders.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const openReassignModal = (wo) => {
    setModalWO(wo);
    setModalOpen(true);
  };

  const closeReassignModal = () => {
    setModalOpen(false);
    setModalWO(null);
  };

  const handleRetryAssign = async (selectedValues) => {
    if (!pendingWorkOrder) return;

    try {
      const updatePayload = {
        ids: [pendingWorkOrder.sku],
        ...selectedValues,
      };
      await updateProduct(updatePayload);

      await assignRoom(
        pendingWorkOrder._id,
        pendingWorkOrder.targetRoom,
        pendingWorkOrder.targetDate,
      );

      toast.success("Product updated and work order reassigned successfully!", {
        duration: 4000,
        position: "top-right",
      });

      setShowAssignErrorModal(false);
      setPendingWorkOrder(null);
      setAssignError(null);
      await fetchWorkOrders();
    } catch (err) {
      console.error("Retry assign failed:", err);
      const msg =
        err.response?.data?.message || "Failed to complete reassignment";
      toast.error(msg, { duration: 5000, position: "top-right" });
    }
  };

  const handleReassign = async (room, date) => {
    const targetDate = date ? new Date(date).toISOString().split("T")[0] : null;

    if (!targetDate || !room) {
      toast.error("Room and date are required",{
        duration: 4000,
        position: "top-right",
      });
      return;
    }

    try {
      await assignRoom(modalWO._id, room, targetDate);

      toast.success(
        `Work order ${modalWO.woNumber} reassigned to ${room} on ${targetDate}`,{
        duration: 4000,
        position: "top-right",
      }
      );
      closeReassignModal();
      await fetchWorkOrders();
    } catch (err) {
      if (err.response?.status === 400) {
        const errorData = err.response.data;

        if (errorData?.error?.includes("Missing product fields")) {
          setPendingWorkOrder({
            ...modalWO,
            targetRoom: room,
            targetDate,
          });
          setAssignError(errorData);
          setShowAssignErrorModal(true);
          return;
        }
      }

      const msg =
        err.response?.data?.message || "Failed to reassign work order";
      toast.error(msg, { duration: 5000, position: "top-right" });
      closeReassignModal();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6 font-sans">
      <Toaster />

      {/* Modal */}
      {modalOpen && modalWO && (
        <ReassignModal
          workOrder={modalWO}
          onClose={closeReassignModal}
          onAssign={handleReassign}
        />
      )}

      <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">
          Alerts: Open Work Orders Older Than {thresholdHours} Hours
        </h2>
        <p className="mb-5 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          Notification only: these work orders are old and pending closure.
          They are not automatically moved or reassigned.
        </p>

        {/* Filters */}
        <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="relative">
            <label
              htmlFor="woNumber"
              className="block text-sm font-medium text-gray-900 mb-1"
            >
              WO Number
            </label>
            <div className="relative">
              <input
                id="woNumber"
                type="text"
                value={filters.woNumber}
                onChange={(e) => updateFilter("woNumber", e.target.value)}
                placeholder="Enter WO Number"
                className="w-full py-2 pl-3 pr-10 rounded border border-gray-300 bg-white text-sm text-gray-900 focus:ring-2 focus:ring-gray-500 focus:outline-none"
              />
              {filters.woNumber && (
                <button
                  onClick={() => clearFilter("woNumber")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="relative">
            <label
              htmlFor="projectNumber"
              className="block text-sm font-medium text-gray-900 mb-1"
            >
              Project Number
            </label>
            <div className="relative">
              <input
                id="projectNumber"
                type="text"
                value={filters.projectNumber}
                onChange={(e) => updateFilter("projectNumber", e.target.value)}
                placeholder="Enter Project Number"
                className="w-full py-2 pl-3 pr-10 rounded border border-gray-300 bg-white text-sm text-gray-900 focus:ring-2 focus:ring-gray-500 focus:outline-none"
              />
              {filters.projectNumber && (
                <button
                  onClick={() => clearFilter("projectNumber")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="dateFilter"
              className="block text-sm font-medium text-gray-900 mb-1"
            >
              Assigned Date
            </label>
            <SingleDatePicker
              id="dateFilter"
              value={filters.date}
              onChange={(date) => updateFilter("date", date)}
              placeholderText="Select date"
              isClearable
              className="w-full py-2 px-3 rounded border border-gray-300 bg-white text-sm text-gray-900 focus:ring-2 focus:ring-gray-500 focus:outline-none"
            />
          </div>
        </div>

        <div className="mb-4">
          <button
            onClick={clearFilters}
            className="py-2 px-4 rounded bg-gray-300 hover:bg-gray-400 text-gray-900 text-sm font-semibold"
          >
            Clear Filters
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse mt-4">
            <thead>
              <tr>
                {[
                  "WO Number",
                  "Project",
                  "SKU",
                  "Title",
                  "Label",
                  "Allergen",
                  "Net Weight",
                  "Planned Qty",
                  "Actual Qty",
                  "Room",
                  "Date",
                  "Action",
                ].map((header) => {
                  const isAction = header === "Action";
                  return (
                    <th
                      key={header}
                      className={`py-3 px-4 text-left bg-gray-200 text-gray-900 font-semibold uppercase text-xs border-b border-gray-300
                        ${isAction ? "text-center" : ""}`}
                    >
                      <div className="flex items-center gap-1">{header}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <SkeletonRow key={i} />
                  ))}
                </>
              ) : error ? (
                <tr>
                  <td colSpan="12" className="text-center py-12 text-red-600">
                    {error}
                  </td>
                </tr>
              ) : paginatedWorkOrders.length > 0 ? (
                paginatedWorkOrders.map((wo) => (
                  <tr
                    key={wo.woNumber}
                    className="hover:bg-gray-50 transition-colors"
                    style={getRowStyle(wo.allergen)}
                  >
                    <td className="py-3 px-4 border-b border-gray-200 text-sm text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis">
                      {wo.woNumber}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200 text-sm text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis">
                      {wo.projectNumber}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200 text-sm text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis">
                      {wo.sku || "N/A"}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200 text-sm text-gray-900">
                      {wo.title}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200 text-sm text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis">
                      {wo.labelCategory || "N/A"}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200 text-sm text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis">
                      {wo.allergen || "None"}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200 text-sm text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis">
                      {wo.netWeight || "N/A"}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200 text-sm text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis">
                      {wo.plannedQty || 0}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200 text-sm text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis">
                      {wo.actualQty || 0}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200 text-sm text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis">
                      {wo.room}
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200 text-sm text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis">
                      <div>{wo.date}</div>
                      <div className="text-xs text-amber-700 mt-1">
                        {wo.alertMessage}
                      </div>
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200 text-sm whitespace-nowrap">
                      {canAssign ? (
                        <button
                          onClick={() => openReassignModal(wo)}
                          className="px-3 py-1 bg-black text-white rounded-lg hover:bg-gray-700 text-sm transition-colors"
                          aria-label={`Re-assign work order ${wo.woNumber}`}
                        >
                          Re-Assign
                        </button>
                      ) : (
                        <button
                          disabled
                          className="px-3 py-1 bg-gray-400 text-gray-600 rounded-lg text-sm cursor-not-allowed"
                          title="You do not have permission to re-assign work orders"
                        >
                          Re-Assign
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="12" className="text-center py-12 text-gray-600">
                    No incomplete work orders.{" "}
                    <button
                      onClick={clearFilters}
                      className="underline text-gray-900"
                    >
                      Clear filters
                    </button>{" "}
                    to view all.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && !error && totalItems > 0 && (
          <div className="mt-6 flex justify-between items-center text-sm text-gray-700">
            <div>
              Showing <strong>{startIndex + 1}</strong> to{" "}
              <strong>{endIndex}</strong> of <strong>{totalItems}</strong>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded border ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white hover:bg-gray-50"
                }`}
              >
                Previous
              </button>

              {(() => {
                const pages = [];
                const showEllipsis = totalPages > 7;
                if (!showEllipsis) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else {
                  pages.push(1);
                  if (currentPage > 3) pages.push("...");
                  const start = Math.max(2, currentPage - 1);
                  const end = Math.min(totalPages - 1, currentPage + 1);
                  for (let i = start; i <= end; i++) pages.push(i);
                  if (currentPage < totalPages - 2) pages.push("...");
                  pages.push(totalPages);
                }
                return pages.map((p, i) =>
                  p === "..." ? (
                    <span key={`e-${i}`} className="px-2">
                      ...
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => handlePageChange(p)}
                      className={`px-3 py-1 rounded border ${
                        currentPage === p
                          ? "bg-black text-white"
                          : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      {p}
                    </button>
                  ),
                );
              })()}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded border ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white hover:bg-gray-50"
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      <AnimatePresence>
        {showAssignErrorModal && (
          <AssignErrorModal
            isOpen={showAssignErrorModal}
            error={assignError}
            onAssign={handleRetryAssign}
            onCancel={() => {
              setShowAssignErrorModal(false);
              setPendingWorkOrder(null);
              setAssignError(null);
            }}
            isLoading={false}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Alerts;
